import { AdminEditViewAccessRights, UserOverview } from "api/generated/model";

import { AccessRightsFilterInputValue } from "./AccessRightsFilterInput";
import { AccessRightKey, CascaderOption } from "./types";

type RightsWithUserIds = Partial<Record<AccessRightKey, number[]>>;

export function buildCascaderOptions(
    facetedValues: Map<AdminEditViewAccessRights, number>,
    users: UserOverview[],
    accessRightLabelMap: Record<AccessRightKey, string>
): CascaderOption[] {
    const rightsAndUserIdRows = [...facetedValues.keys()];
    const rightsWithUserIds = rightsAndUserIdRows.reduce<RightsWithUserIds>(
        (acc, row) => {
            const admin = [
                ...new Set([...(acc.admin ?? []), ...(row.admin ?? [])]),
            ];
            const edit = [
                ...new Set([...(acc.edit ?? []), ...(row.edit ?? [])]),
            ];
            const view = [
                ...new Set([...(acc.view ?? []), ...(row.view ?? [])]),
            ];

            return {
                ...(admin.length > 0 && { admin }),
                ...(edit.length > 0 && { edit }),
                ...(view.length > 0 && { view }),
            };
        },
        {}
    );

    const entries = Object.entries(rightsWithUserIds) as [
        AccessRightKey,
        number[],
    ][];

    const collator = new Intl.Collator();
    return entries.map(([key, value]) => ({
        value: key,
        label: accessRightLabelMap[key],
        children: Array.from(value)
            .map((id) => {
                const user = users.find((user) => user.id === id);
                if (!user) {
                    throw new Error(`User with id ${id} not found`);
                }
                const label = `${user.firstname} ${user.lastname}`;
                return {
                    value: id,
                    label,
                };
            })
            .sort((a, b) => collator.compare(a.label, b.label)),
    }));
}

export function accessRightsFilterFunction(
    rowAccessRights: Partial<AdminEditViewAccessRights>,
    filterValue: AccessRightsFilterInputValue
) {
    if (!filterValue) {
        return true;
    }
    if (filterValue.length === 0) {
        return true;
    }
    for (const filter of filterValue) {
        const [right, user] = filter;

        if (right in rowAccessRights) {
            const users = rowAccessRights[right];
            if (!users) {
                return false;
            }

            // If no user is specified, any user should be included
            if (user === undefined) {
                if (users.length > 0) {
                    return true;
                }
            } else if (users.includes(user)) {
                return true;
            }
        }
    }
    return false;
}
